import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  getPosts,
  updatePost,
  deletePost,
  togglePostReaction,
  markFeedSeen,
} from "../../utils/firebasefunctions";
import { newmessagesuccess } from "../../utils/helper";
import { applyReactionDeltas, computeNewReactionState } from "../../utils/feedHelpers";
import PostComposer from "./PostComposer";
import PostCard from "./PostCard";
import PostEditModal from "./PostEditModal";

function FeedPanel() {
  const [{ user, posts, postsHasMore, postsCursor }, dispatch] =
    useStateValue();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  // Initial fetch when the panel mounts (and posts not already in context).
  useEffect(() => {
    if (posts.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { posts: page, nextCursor, hasMore } = await getPosts({});
        if (!cancelled) {
          dispatch({
            type: actionType.STORE_POSTS,
            payload: { posts: page, nextCursor, hasMore },
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load feed:", err);
          toast.error("Failed to load feed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening the feed clears the unread badge: stamp lastSeenFeedAt on the user
  // doc and update it in context so the sidebar badge drops to 0 immediately.
  useEffect(() => {
    if (!user?.id) return;
    markFeedSeen(user.id);
    dispatch({
      type: actionType.UPDATEUSER,
      payload: { ...user, lastSeenFeedAt: Date.now() },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleCreated = (newPost) => {
    dispatch({ type: actionType.ADD_POST, payload: newPost });
  };

  const handleReact = async (post, reaction) => {
    // Optimistic update.
    const prev = post.reactions?.[user.id] || null;
    const { newReaction, deltas } = computeNewReactionState(prev, reaction);
    const optimisticReactions = { ...(post.reactions || {}) };
    if (newReaction === null) {
      delete optimisticReactions[user.id];
    } else {
      optimisticReactions[user.id] = newReaction;
    }
    const optimisticCounts = applyReactionDeltas(post.reactionCounts, deltas);
    dispatch({
      type: actionType.UPDATE_POST,
      payload: {
        id: post.id,
        reactions: optimisticReactions,
        reactionCounts: optimisticCounts,
      },
    });

    try {
      const updated = await togglePostReaction(post.id, user.id, reaction);
      dispatch({
        type: actionType.UPDATE_POST,
        payload: {
          id: post.id,
          reactions: updated.reactions,
          reactionCounts: updated.reactionCounts,
        },
      });

      // Notify the author when someone reacts for the first time.
      // Skip if: (a) the user is reacting to their own post,
      //          (b) this is a toggle-off (prev was non-null, new is null),
      //          (c) this is a switch like↔dislike (prev was non-null).
      const userReactionNow = updated.reactions?.[user.id] || null;
      const isNewReaction = prev === null && userReactionNow !== null;
      const isSelfReaction = post.authorId === user.id;
      if (isNewReaction && !isSelfReaction && post.authorId) {
        const reactorName =
          `${user.firstname || ""} ${user.lastname || ""}`.trim() ||
          "Someone";
        const VERBS = {
          like: "liked",
          dislike: "disliked",
          heart: "loved",
          party: "celebrated",
        };
        const verb = VERBS[userReactionNow] || "reacted to";
        const postPreview = (post.text || "").trim().slice(0, 60) || "your post";
        try {
          await newmessagesuccess(
            `${reactorName} ${verb} your post`,
            post.authorId,
            postPreview,
            String(post.id || ""),
            "post-reaction",
            String(post.id || ""),
          );
        } catch (notifyErr) {
          // Don't disrupt the reaction flow on notification failure.
          console.error("Failed to notify post author:", notifyErr);
        }
      }
    } catch (err) {
      console.error("Failed to react:", err);
      // Revert optimistic update.
      dispatch({
        type: actionType.UPDATE_POST,
        payload: {
          id: post.id,
          reactions: post.reactions || {},
          reactionCounts: applyReactionDeltas(post.reactionCounts, {}),
        },
      });
    }
  };

  const handleEditSave = async (newText) => {
    if (!editingPost) return;
    try {
      await updatePost(editingPost.id, { text: newText });
      dispatch({
        type: actionType.UPDATE_POST,
        payload: { id: editingPost.id, text: newText, updatedAt: Date.now() },
      });
      setEditingPost(null);
    } catch (err) {
      console.error("Failed to edit post:", err);
      toast.error("Failed to save");
      throw err;
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await deletePost(post.id);
      dispatch({ type: actionType.REMOVE_POST, payload: { id: post.id } });
    } catch (err) {
      console.error("Failed to delete post:", err);
      toast.error("Failed to delete");
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || !postsHasMore) return;
    setLoadingMore(true);
    try {
      const { posts: page, nextCursor, hasMore } = await getPosts({
        cursor: postsCursor,
      });
      dispatch({
        type: actionType.APPEND_POSTS,
        payload: { posts: page, nextCursor, hasMore },
      });
    } catch (err) {
      console.error("Failed to load more posts:", err);
      toast.error("Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      <PostComposer currentUser={user} onCreated={handleCreated} />

      {loading && posts.length === 0 && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-white border border-black/[0.04] rounded-xl p-5 animate-pulse"
            >
              <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            </div>
          ))}
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="bg-white border border-black/[0.04] rounded-xl p-10 text-center">
          <div className="text-[14px] font-semibold text-slate-700">
            Nothing posted yet
          </div>
          <div className="text-[12px] text-gray-400 mt-1">
            Start the conversation above.
          </div>
        </div>
      )}

      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          currentUser={user}
          onReact={(reaction) => handleReact(post, reaction)}
          onEdit={() => setEditingPost(post)}
          onDelete={() => handleDelete(post)}
        />
      ))}

      {postsHasMore && posts.length > 0 && (
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-[12.5px] font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-white disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {editingPost && (
        <PostEditModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
}

export default FeedPanel;
